import {
  Box,
  Card,
  Flex,
  Grid,
  SegmentedControl,
  Text,
} from '@radix-ui/themes';
import { BreakCanvas } from 'classes/break-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { TVHelper } from 'classes/helpers/traj-visualizer.helper';
import { PlateCanvas } from 'classes/plate-canvas';
import { BreakView } from 'components/common/break-view';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTrainingStatusIcon } from 'components/common/pitch-lists/training-status-icon';
import { CommonTooltip } from 'components/common/tooltip';
import { TrainingDataTable } from 'components/common/training-data-table';
import { TrajectoryView } from 'components/common/trajectory-view';
import { MachineContext } from 'contexts/machine.context';
import { DOT_SIZE_MD } from 'enums/canvas';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { ITVDefinition } from 'interfaces/traj-visualizer/i-definition';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { DEFAULT_PLATE, IPitch, IPlateLocExt } from 'lib_ts/interfaces/pitches';
import { DEFAULT_SEAMS } from 'lib_ts/interfaces/pitches/i-base';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clone, uniq } from 'lodash';
import {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-input-slider';
import { UserMachineModelsService } from 'services/machine-models.service';
import useMiscStore from 'stores/use-misc-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const REDRAW_DEQUEUE_MS = 100;

interface IProps {
  pitch: IPitch;
  shots: IMachineShot[];
  ms?: IMachineState;
  onTrainFn?: TrainPitchesFn;
}

enum TabKey {
  Trajectory = '3D',
  Graph = 'Graph',
}

const PlotContainer = (props: PropsWithChildren) => (
  <Card style={{ padding: '0' }}>{props.children}</Card>
);

interface IPlatePlotProps {
  pitch: IPitch;
  shots: IMachineShot[];
}

const PlatePlot = (props: IPlatePlotProps) => {
  const COMPONENT_NAME = 'TrainingDatePlatePlot';

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(() => {
    switch (props.pitch.priority) {
      case BuildPriority.Breaks: {
        return PlateCanvas.makePresetTraining(
          VISIBLE_STRIKEZONE,
          isDarkMode,
          1
        );
      }

      default: {
        return PlateCanvas.makePresetTraining(
          VISIBLE_STRIKEZONE,
          isDarkMode,
          2
        );
      }
    }
  }, [props.pitch.priority, isDarkMode]);

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const miscCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        mainCanvas.current,
        miscCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = mainCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        canvasDef.drawPresetTrainingRulersInches(ctx, 10);
        await canvasDef.drawGround(ctx);
        canvasDef.drawStrikeZoneAutoAdjustArea(ctx, '#304384');
        canvasDef.drawStrikeZone(ctx, true);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef]);

  // drawMisc
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = miscCanvas.current;
        if (!canvas) {
          console.warn('No misc canvas found');
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.warn('No misc canvas context found');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        await canvasDef.drawTarget(ctx, {
          as: 'cross',
          color: `rgb(${StringHelper.hexToRgb('#3E63DD').join(',')})`,
          target: {
            plate_x: canvasDef.CONFIG.x.defaultFt,
            plate_z: canvasDef.CONFIG.y.defaultFt,
          },
          width_px: 16,
          // clear canvas right before drawing to avoid race condition where pitch/step changes while mid-draw
          beforeDraw: () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          },
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef, props.pitch]);

  // drawShots
  useEffect(() => {
    try {
      const canvas = shotsCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;

      const sortedShots = props.shots.sort((a, b) =>
        StringHelper.safeSort(a._created, b._created)
      );

      // don't draw shots since drawMisc should draw the checkmark instead
      const plates = sortedShots.map((shot) => {
        const o: IPlateLocExt = {
          ...TrajHelper.getPlateLoc(shot.traj),
          _created: shot._created,
          _id: shot._id,
        };

        return o;
      });

      plates.forEach((p, i) => {
        canvasDef.drawTrainingDot(ctx, {
          plate: p,
          isNew: false,
          size: DOT_SIZE_MD,
          index: i + 1,
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef, props.shots]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <PlotContainer>
        <div className="slider">
          <div
            className="sizer"
            style={{ aspectRatio: canvasDef.CONFIG.canvas.aspectRatio }}
          >
            {/* for log rocket recordings to show the results */}
            {imgSrc && <img className="image-wrapper" src={imgSrc} />}
            <canvas
              ref={mergeCanvas}
              data-identifier="merge-canvas"
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            <canvas
              ref={mainCanvas}
              data-identifier="main-canvas"
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            <canvas
              ref={miscCanvas}
              data-identifier="misc-canvas"
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            <canvas
              ref={shotsCanvas}
              data-identifier="shots-canvas"
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            <div
              className={StringHelper.classNames([
                'slider-wrapper',
                'animate-fade',
              ])}
            >
              <Slider
                data-testid="QuickTrainPlateLocation"
                axis="xy"
                xstep={canvasDef.CONFIG.x.step}
                xmin={canvasDef.CONFIG.x.minFt}
                xmax={canvasDef.CONFIG.x.maxFt}
                x={canvasDef.CONFIG.x.defaultFt}
                ystep={canvasDef.CONFIG.y.step}
                ymin={canvasDef.CONFIG.y.minFt}
                ymax={canvasDef.CONFIG.y.maxFt}
                y={canvasDef.CONFIG.y.defaultFt}
                styles={{
                  track: {
                    backgroundColor: 'rgba(0, 0, 255, 0)',
                    width: '100%',
                    height: '100%',
                  },
                  thumb: {
                    // draw the crosshair as svg instead of showing the slider thumb
                    backgroundColor: 'rgba(0, 255, 0, 0)',
                    backgroundImage: 'url(/img/crosshair.svg)',
                    backgroundSize: 'cover',
                    boxShadow: 'none',
                    height: 32,
                    width: 32,
                    margin: '-16px',
                  },
                }}
                yreverse
              />
            </div>
          </div>
        </div>
      </PlotContainer>
    </ErrorBoundary>
  );
};

interface IBreakPlotProps {
  pitch: IPitch;
  shots: IMachineShot[];
}

const BreakPlot = (props: IBreakPlotProps) => {
  const { pitch, shots } = props;

  const actual = BreakCanvas.getMedian(
    shots.map((s) => TrajHelper.getBreaksFromShot(s))
  );

  const actual_x_in = actual ? actual.xInches : 0;
  const actual_z_in = actual ? actual.zInches : 0;

  const target = pitch.breaks
    ? (pitch.breaks ?? TrajHelper.getBreaks(pitch.traj))
    : undefined;

  const target_x_in = target ? -target.xInches : actual_x_in;
  const target_z_in = target ? target.zInches : actual_z_in;

  return (
    <PlotContainer>
      <BreakView
        actual_x_in={actual_x_in}
        actual_z_in={actual_z_in}
        target_x_in={target_x_in}
        target_z_in={target_z_in}
        shots={shots}
        disabled
        training
      />
    </PlotContainer>
  );
};

export const TrainingDataTab = (props: IProps) => {
  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const { machine } = useContext(MachineContext);

  const [activePlateTab, setActivePlateTab] = useState<TabKey>(
    TabKey.Trajectory
  );

  // state for discarded shots based on current TMS record (if any)
  const [discardedIDs, setDiscardedIDs] = useState<string[]>(() => {
    const tms = useTrainedMsStore.getState().getTms(props.pitch.bs_hash);
    return tms?.discardedShotIDs ?? [];
  });

  // keep a local copy of the shots to avoid re-rendering the plots when discarded shots change
  const plotShots = useMemo(
    () => props.shots.filter((s) => !discardedIDs.includes(s._id)),
    [props.shots, discardedIDs]
  );

  const [model, setModel] = useState<IMachineModel>();

  useEffect(() => {
    const getMs = async () => {
      try {
        const ms = await useTrainedMsStore.getState().lazyLoadMs({
          machine: machine,
          bs_hash: props.pitch.bs_hash,
          chars: {
            build_id: props.pitch._id,
            priority: props.pitch.priority ?? BuildPriority.Default,
            bs: props.pitch.bs,
            bs_hash: props.pitch.bs_hash,
            traj: props.pitch.traj,
            plate: props.pitch.plate_loc_backup ?? DEFAULT_PLATE,
            seams: props.pitch.seams ?? DEFAULT_SEAMS,
            breaks: props.pitch.breaks,
          },
        });

        if (!ms?.model_id) {
          setModel(undefined);
          return;
        }

        const result = await UserMachineModelsService.getInstance().getModel(
          ms.model_id
        );

        setModel(result);
      } catch (e) {
        console.error(e);
        setModel(undefined);
      }
    };

    getMs();
  }, [machine, props.pitch]);

  const trajModels = useMemo<ITVDefinition[]>(() => {
    const { pitch, ms } = props;

    if (!ms) {
      return [];
    }

    const output = Object.values(
      TVHelper.getSummary({
        shots: plotShots,
        bs: pitch.bs,
        traj: pitch.traj,
        ms: ms,
        releaseFt: machine.releaseFt,
        includeEachActual: true,
      })
    ).flatMap((v) => v);

    return output;
  }, [props.pitch, props.ms, machine.releaseFt, plotShots]);

  const showBreaks = props.pitch.priority === BuildPriority.Breaks;

  const isTrained = !!props.pitch._mx_tms?._mx_trained;

  return (
    <ErrorBoundary identifier="TrainingDataTable">
      <Flex direction="column" gap={showBreaks ? '2' : '3'}>
        <Flex justify="between" align="center">
          <Flex gap="2" align="center">
            <Text size={RADIX.TEXT.SIZE.MD} weight="bold">
              {t('common.training-data')}
            </Text>
            <CommonTooltip
              trigger={
                <Box>
                  <CommonTrainingStatusIcon
                    tms={props.pitch._mx_tms}
                    last_hardware_changed={machine.last_hardware_changed}
                    autoRefresh={false}
                    canRefresh={false}
                    modelName={model?.name}
                  />
                </Box>
              }
              text_md={PitchListHelper.getTrainedStatusTooltip({
                tms: props.pitch._mx_tms,
                last_hardware_changed: machine.last_hardware_changed,
                modelName: model?.name,
              })}
            />
          </Flex>
          {props.onTrainFn && (
            <Box>
              <DialogButton
                label={
                  isTrained ? 'common.continue-training' : 'common.train-pitch'
                }
                variant="solid"
                color={RADIX.COLOR.WARNING}
                disabled={!readyToTrain}
                onClick={() => props.onTrainFn?.([props.pitch._id], isTrained)}
              />
            </Box>
          )}
        </Flex>
        <Grid columns={showBreaks ? '2' : '1'} gap={RADIX.FLEX.GAP.MD}>
          {/* header w/ toggle */}
          <Flex justify="between" align="center" style={{ minHeight: '24px' }}>
            <Text size="2" weight="bold">
              {t('pd.plate-location')}
            </Text>
            <Box>
              <SegmentedControl.Root
                value={activePlateTab}
                onValueChange={(v) => setActivePlateTab(v as TabKey)}
                style={{ width: '100%' }}
                size="1"
              >
                {Object.values(TabKey).map((o) => (
                  <SegmentedControl.Item key={`mode-${o}`} value={o}>
                    <Box px={RADIX.FLEX.PAD.MD}>{t(o)}</Box>
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </Box>
          </Flex>

          {/* header only - this weirdness is to force the headers to be the same height in the grid */}
          {showBreaks && (
            <Text size="2" weight="bold">
              {t('pd.breaks')}
            </Text>
          )}

          {activePlateTab === TabKey.Trajectory ? (
            <TrajectoryView
              identifier="PitchDataTrajView"
              definitions={trajModels}
              startingZoom={1.75}
              aspectRatio={showBreaks ? '1' : undefined}
              showCameraControls
            />
          ) : (
            <PlatePlot pitch={props.pitch} shots={plotShots} />
          )}

          {showBreaks && (
            <Box
              style={{
                width: '100%',
                aspectRatio: '1',
              }}
            >
              <BreakPlot pitch={props.pitch} shots={plotShots} />
            </Box>
          )}
        </Grid>
      </Flex>
      <Box>
        <TrainingDataTable
          data-identifier="PitchDetailsDataTable"
          maxSamples={0}
          priority={props.pitch.priority ?? BuildPriority.Spins}
          targetBS={props.pitch.bs}
          targetTraj={props.pitch.traj}
          targetBreaks={
            props.pitch.breaks ?? TrajHelper.getBreaks(props.pitch.traj)
          }
          actualShots={props.shots}
          discardedShotIDs={discardedIDs}
          onDiscard={async (shot) => {
            // in case of error, revert to previous state
            const backupDiscarded = clone(discardedIDs);

            const nextDiscarded = uniq(
              discardedIDs.includes(shot._id)
                ? // undo the discard
                  discardedIDs.filter((id) => id !== shot._id)
                : // add the shot to the discarded list
                  [...discardedIDs, shot._id]
            );

            const tms = useTrainedMsStore
              .getState()
              .getTms(props.pitch.bs_hash);

            if (!tms) {
              // cannot discard if no TMS record exists
              return;
            }

            if (
              tms.discardedShotIDs &&
              ArrayHelper.checkSame(nextDiscarded, tms.discardedShotIDs)
            ) {
              // no change
              return;
            }

            // instant UI update
            setDiscardedIDs(nextDiscarded);

            const updated = await useTrainedMsStore.getState().upsertTms({
              machineID: tms.machineID,
              ball_type: tms.ball_type,
              bs_hash: tms.bs_hash,
              aimingTraj: null,
              discardedShotIDs: nextDiscarded,
              deleted: false,
            });

            if (!updated) {
              // error updating TMS record, revert to previous state
              setDiscardedIDs(backupDiscarded);
              return;
            }

            console.assert(
              ArrayHelper.checkSame(
                updated.discardedShotIDs ?? [],
                nextDiscarded
              ),
              'Discarded shot IDs mismatch'
            );
          }}
          discardDisabled
        />
      </Box>
    </ErrorBoundary>
  );
};
